import { Loader2, MapPinOff, X } from "lucide-react";
import { lazy, Suspense, useEffect, useRef } from "react";
import { albumFilterOptions } from "@/components/photos/PhotoFilters";
import { PhotoViewer } from "@/components/photos/PhotoViewer";
import { EmptyState } from "@/components/shared/empty-state";
import { EnumSelect } from "@/components/shared/enum-select";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { type MapFilters, usePins, useProjectPhoto } from "@/hooks/use-map";
import { ApiError } from "@/lib/api-client";
import type { ProjectDetail } from "@/schemas/photos";

// Leaflet and its clustering are loaded the first time this tab opens, not with the app.
const PhotoMap = lazy(() =>
  import("@/components/map/PhotoMap").then((module) => ({ default: module.PhotoMap })),
);

function noLocationText(count: number) {
  return `${count.toLocaleString("en-US")} ${count === 1 ? "photo has" : "photos have"} no location`;
}

// Worded as the Photos tab words them ("File not pulled for this POC").
function notPulledText(count: number) {
  return `${count.toLocaleString("en-US")} ${count === 1 ? "file was" : "files were"} not pulled for this POC, so there is no photo or location to show`;
}

function notImagesText(count: number) {
  return `${count.toLocaleString("en-US")} ${count === 1 ? "file is" : "files are"} not an image, so ${count === 1 ? "it has" : "they have"} no location to show`;
}

export function MapTab({
  project,
  filters,
  openPhotoId,
  canShare,
  onChange,
  onClear,
  onViewPhoto,
}: {
  project: ProjectDetail;
  filters: MapFilters;
  /** The photo named in the URL, if any. */
  openPhotoId: string | undefined;
  /** PM or admin: the viewer shows sharing state and its controls. */
  canShare: boolean;
  onChange: (next: Partial<MapFilters>) => void;
  onClear: () => void;
  /** Open or close the viewer. `push` adds a history entry, so back closes it. */
  onViewPhoto: (id: string | undefined, how: "push" | "replace" | "close") => void;
}) {
  const query = usePins(project.id, filters);
  const photo = useProjectPhoto(project.id, openPhotoId);
  // True while the viewer sits on a history entry this tab pushed, so Close can go back.
  const pushed = useRef(false);

  useEffect(() => {
    if (!openPhotoId) pushed.current = false;
  }, [openPhotoId]);

  // A link can name a photo this account can not see, or that is gone: drop it. Any other
  // failure (the network, the server) is shown, not hidden by closing the viewer.
  const gone = photo.error instanceof ApiError && photo.error.status === 404;
  useEffect(() => {
    if (openPhotoId && gone) onViewPhoto(undefined, "replace");
  }, [openPhotoId, gone, onViewPhoto]);

  const closeViewer = () => {
    const goBack = pushed.current;
    pushed.current = false;
    onViewPhoto(undefined, goBack ? "close" : "replace");
  };

  // The previous filters' answer stays in the query while the new one loads: it is not
  // drawn, fitted to or counted, so the map never shows one filter under another's label.
  const loading = query.isPlaceholderData;
  const data = loading ? undefined : query.data;
  const located = data?.pins.length ?? 0;
  const noLocation = data?.noLocation ?? 0;
  const notPulled = data?.notPulled ?? 0;
  const notImages = data?.notImages ?? 0;
  const filtered = Boolean(filters.album || filters.from || filters.to);
  const empty =
    data !== undefined && located === 0 && noLocation === 0 && notPulled === 0 && notImages === 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 items-end gap-2 md:flex md:flex-wrap">
        <EnumSelect
          aria-label="Album"
          value={filters.album ?? ""}
          onValueChange={(album) => onChange({ album: album || undefined })}
          options={albumFilterOptions(project)}
          allOption={{ label: "All albums" }}
          className="col-span-2 w-full min-w-0 md:w-56"
        />
        <div className="space-y-1">
          <Label htmlFor="map-from" className="text-xs text-muted-foreground">
            From
          </Label>
          <Input
            id="map-from"
            type="date"
            value={filters.from ?? ""}
            onChange={(event) => onChange({ from: event.target.value || undefined })}
            className="w-full md:w-40"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="map-to" className="text-xs text-muted-foreground">
            To
          </Label>
          <Input
            id="map-to"
            type="date"
            value={filters.to ?? ""}
            onChange={(event) => onChange({ to: event.target.value || undefined })}
            className="w-full md:w-40"
          />
        </div>
        {filtered && (
          <Button variant="ghost" onClick={onClear} className="w-full md:w-auto">
            <X className="size-4" />
            Clear filters
          </Button>
        )}
      </div>

      {data && (
        <div className="space-y-1 text-sm text-muted-foreground" data-testid="map-counts">
          <p data-testid="map-located" className="tabular-nums">
            {located.toLocaleString("en-US")} {located === 1 ? "photo" : "photos"} on the map
          </p>
          {notPulled > 0 && <p data-testid="map-not-pulled">{notPulledText(notPulled)}.</p>}
          {notImages > 0 && <p data-testid="map-not-images">{notImagesText(notImages)}.</p>}
          {noLocation > 0 && (
            <p data-testid="map-no-location">
              {noLocationText(noLocation)}.{" "}
              {data.approximate
                ? "They are drawn as dashed ? pins at the middle of the project's other photos, not where they were taken."
                : "There are no other photos to place them near, so they are not drawn."}
            </p>
          )}
        </div>
      )}

      {query.isError ? (
        <ErrorState message="Could not load the map" onRetry={() => query.refetch()} />
      ) : empty ? (
        <EmptyState
          icon={MapPinOff}
          title="No photos to show"
          description={
            filtered ? "No photos match these filters." : "This project has no photos you can see."
          }
          className="rounded-lg border border-dashed border-border py-16"
        />
      ) : (
        // `isolate` keeps Leaflet's panes (z-index up to 1000) inside this box, under the viewer and menus.
        <div className="relative isolate h-[calc(100dvh-30rem)] min-h-80 overflow-hidden rounded-lg border border-border">
          {query.data ? (
            <Suspense fallback={<Skeleton className="h-full w-full" />}>
              <PhotoMap
                data={data}
                fitKey={JSON.stringify(filters)}
                onOpen={(id) => {
                  pushed.current = true;
                  onViewPhoto(id, "push");
                }}
              />
            </Suspense>
          ) : (
            <Skeleton className="h-full w-full" />
          )}
          {loading && (
            <div
              data-testid="map-loading"
              className="absolute inset-0 z-[1000] flex items-center justify-center bg-background/60 text-sm text-muted-foreground"
            >
              <Loader2 className="mr-2 size-4 animate-spin" />
              Loading photos
            </div>
          )}
        </div>
      )}

      {openPhotoId && photo.data && (
        <PhotoViewer
          photos={[photo.data]}
          total={1}
          albums={project.albums}
          index={0}
          hasNext={false}
          onIndexChange={() => undefined}
          onLoadMore={() => undefined}
          sharingProjectId={canShare ? project.id : undefined}
          onClose={closeViewer}
        />
      )}
      {openPhotoId && !photo.data && !gone && (
        <Dialog open onOpenChange={(open) => !open && closeViewer()}>
          <DialogContent data-testid="viewer-status">
            <DialogTitle>
              {photo.isError ? "Could not open the photo" : "Opening photo"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {photo.isError ? "The photo did not load." : "The photo is loading."}
            </DialogDescription>
            {photo.isError ? (
              <ErrorState message="Could not load this photo" onRetry={() => photo.refetch()} />
            ) : (
              <div
                data-testid="viewer-loading"
                className="flex items-center justify-center py-12 text-sm text-muted-foreground"
              >
                <Loader2 className="mr-2 size-4 animate-spin" />
                Loading photo
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
